export type TeamCapacityContributorSummary = {
  userId: string;
  uid: string;
  name: string;
  email: string | null;
  photoURL: string | null;
  role: string | null;
  assignedTicketCount: number;
  remainingEstimateMinutes: number;
  loadHours: number;
  capacityRatio: number;
  remainingCapacityMinutes: number;
  isLead: boolean;
  membershipRole: string | null;
  isPrimaryMembership: boolean;
  isRosterMember: boolean;
  isOutsideRoster: boolean;
};

export type TeamCapacitySummary = {
  teamNodeId: string;
  orgId: string;
  teamLabel: string;
  teamStatus: string;
  roundRobinEnabled: boolean;
  teamCapacityHours: number;
  contributorCount: number;
  trackedTicketCount: number;
  assignedTicketCount: number;
  unassignedTicketCount: number;
  openTicketCount: number;
  inProgressTicketCount: number;
  blockedTicketCount: number;
  queuedMinutes: number;
  assignedMinutes: number;
  unassignedMinutes: number;
  totalCapacityMinutes: number;
  remainingCapacityMinutes: number;
  overloaded: boolean;
  appointedLead:
    | {
        userId: string;
        uid: string;
        email: string | null;
        name: string;
        photoURL: string | null;
        role: string | null;
      }
    | null;
  contributors: TeamCapacityContributorSummary[];
};

function cleanText(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function toNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toBoolean(value: unknown) {
  return value === true;
}

export function normalizeTeamCapacitySummaryPayload(raw: unknown): TeamCapacitySummary | null {
  const summary =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? 'summary' in raw && raw.summary && typeof raw.summary === 'object' && !Array.isArray(raw.summary)
        ? (raw.summary as Record<string, unknown>)
        : (raw as Record<string, unknown>)
      : null;
  if (!summary) return null;

  const teamNodeId = cleanText(summary.teamNodeId);
  const orgId = cleanText(summary.orgId);
  if (!teamNodeId || !orgId) return null;

  const contributors = Array.isArray(summary.contributors)
    ? summary.contributors
        .map((entry): TeamCapacityContributorSummary | null => {
          if (!entry || typeof entry !== 'object') return null;
          const record = entry as Record<string, unknown>;
          const uid = cleanText(record.uid);
          const userId = cleanText(record.userId);
          if (!uid || !userId) return null;
          return {
            userId,
            uid,
            name: cleanText(record.name) || uid,
            email: cleanText(record.email) || null,
            photoURL: cleanText(record.photoURL) || null,
            role: cleanText(record.role) || null,
            assignedTicketCount: Math.max(0, Math.round(toNumber(record.assignedTicketCount))),
            remainingEstimateMinutes: Math.max(0, Math.round(toNumber(record.remainingEstimateMinutes))),
            loadHours: Math.max(0, toNumber(record.loadHours)),
            capacityRatio: Math.max(0, Math.min(1, toNumber(record.capacityRatio))),
            remainingCapacityMinutes: Math.max(0, Math.round(toNumber(record.remainingCapacityMinutes))),
            isLead: toBoolean(record.isLead),
            membershipRole: cleanText(record.membershipRole) || null,
            isPrimaryMembership: toBoolean(record.isPrimaryMembership),
            isRosterMember: toBoolean(record.isRosterMember),
            isOutsideRoster: toBoolean(record.isOutsideRoster),
          };
        })
        .filter((entry): entry is TeamCapacityContributorSummary => Boolean(entry))
    : [];

  const rawLead =
    summary.appointedLead &&
    typeof summary.appointedLead === 'object' &&
    !Array.isArray(summary.appointedLead)
      ? (summary.appointedLead as Record<string, unknown>)
      : null;

  return {
    teamNodeId,
    orgId,
    teamLabel: cleanText(summary.teamLabel) || teamNodeId,
    teamStatus: cleanText(summary.teamStatus) || 'active',
    roundRobinEnabled: toBoolean(summary.roundRobinEnabled),
    teamCapacityHours: Math.max(1, Math.round(toNumber(summary.teamCapacityHours) || 30)),
    contributorCount: Math.max(0, Math.round(toNumber(summary.contributorCount))),
    trackedTicketCount: Math.max(0, Math.round(toNumber(summary.trackedTicketCount))),
    assignedTicketCount: Math.max(0, Math.round(toNumber(summary.assignedTicketCount))),
    unassignedTicketCount: Math.max(0, Math.round(toNumber(summary.unassignedTicketCount))),
    openTicketCount: Math.max(0, Math.round(toNumber(summary.openTicketCount))),
    inProgressTicketCount: Math.max(0, Math.round(toNumber(summary.inProgressTicketCount))),
    blockedTicketCount: Math.max(0, Math.round(toNumber(summary.blockedTicketCount))),
    queuedMinutes: Math.max(0, Math.round(toNumber(summary.queuedMinutes))),
    assignedMinutes: Math.max(0, Math.round(toNumber(summary.assignedMinutes))),
    unassignedMinutes: Math.max(0, Math.round(toNumber(summary.unassignedMinutes))),
    totalCapacityMinutes: Math.max(0, Math.round(toNumber(summary.totalCapacityMinutes))),
    remainingCapacityMinutes: Math.round(toNumber(summary.remainingCapacityMinutes)),
    overloaded: toBoolean(summary.overloaded),
    appointedLead:
      rawLead && cleanText(rawLead.uid) && cleanText(rawLead.userId)
        ? {
            userId: cleanText(rawLead.userId),
            uid: cleanText(rawLead.uid),
            email: cleanText(rawLead.email) || null,
            name: cleanText(rawLead.name) || cleanText(rawLead.uid),
            photoURL: cleanText(rawLead.photoURL) || null,
            role: cleanText(rawLead.role) || null,
          }
        : null,
    contributors,
  };
}
